import { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RiSparklingLine, RiSendPlaneLine, RiImageAddLine, RiCloseLine } from '@remixicon/react';
import { useSession } from '@/lib/auth-client';
import { toast } from 'sonner';
import { api } from '@/lib/api';

interface FeedbackDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FeedbackDialog({ open, onOpenChange }: FeedbackDialogProps) {
  const { data: session } = useSession();
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!feedback.trim()) {
      toast.error('Please enter your feedback');
      return;
    }

    setIsSubmitting(true);

    try {
      // Convert image to base64 if present
      let imageBase64 = null;
      if (imageFile) {
        const reader = new FileReader();
        imageBase64 = await new Promise<string>((resolve) => {
          reader.onloadend = () => resolve(reader.result as string);
          reader.readAsDataURL(imageFile);
        });
      }

      // Send feedback via backend API
      const response = await api.sendFeedback({
        userId: session?.user?.id,
        userName: session?.user?.name || 'Anonymous',
        userEmail: session?.user?.email || 'Not provided',
        feedback: feedback,
        image: imageBase64, // Add image data
      });

      if (!response) {
        throw new Error('Failed to send feedback');
      }

      toast.success('Thank you for your feedback!', {
        description: 'We appreciate you taking the time to help us improve Moniva.',
      });

      setFeedback('');
      handleRemoveImage();
      onOpenChange(false);
    } catch (error: any) {
      console.error('Error sending feedback:', error);
      toast.error('Failed to send feedback', {
        description: error.message || 'Please try again later or contact support@usemoniva.com directly.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center gap-2">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
              <RiSparklingLine className="w-5 h-5 text-primary" />
            </div>
            <div>
              <DialogTitle>Share Your Feedback</DialogTitle>
              <DialogDescription>
                Help us improve Moniva with your suggestions
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="mt-4 space-y-4 overflow-y-auto flex-1 pr-2 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
          <div className="space-y-2">
            <Label htmlFor="email">Your Email</Label>
            <Input
              id="email"
              type="email"
              value={session?.user?.email || ''}
              disabled
              className="bg-muted"
            />
            <p className="text-xs text-muted-foreground">
              We'll use this to follow up on your feedback
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="feedback">Your Feedback</Label>
            <Textarea
              id="feedback"
              placeholder="Tell us what you think, what features you'd like to see, or any issues you've encountered..."
              value={feedback}
              onChange={(e) => {
                if (e.target.value.length <= 1000) {
                  setFeedback(e.target.value);
                }
              }}
              rows={6}
              className="resize-none"
              required
            />
            <p className="text-xs text-muted-foreground">
              {feedback.length}/1000 characters
            </p>
          </div>

          {/* Image Upload (Optional) */}
          <div className="space-y-2">
            <Label htmlFor="image">Attachment (optional)</Label>
            <div className="space-y-2">
              {!imagePreview ? (
                <div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    id="image"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full gap-2"
                  >
                    <RiImageAddLine className="w-4 h-4" />
                    Add Screenshot or Image
                  </Button>
                  <p className="text-xs text-muted-foreground mt-1">
                    Max 5MB • PNG, JPG, or GIF
                  </p>
                </div>
              ) : (
                <div className="relative border rounded-lg p-2">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-auto rounded"
                  />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    onClick={handleRemoveImage}
                    className="absolute top-3 right-3 h-7 w-7"
                  >
                    <RiCloseLine className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !feedback.trim()}
              className="gap-2"
            >
              {isSubmitting ? (
                'Sending...'
              ) : (
                <>
                  Send Feedback
                  <RiSendPlaneLine className="w-4 h-4" />
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
